'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/Button';
import { useAnalytics } from '@/hooks/useAnalytics';
import Image from 'next/image';
import Link from 'next/link';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6 }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Team member data
const team = [
  {
    name: 'Allen Ginder',
    role: 'Chief Executive Officer',
    image: '/images/team/silhouette-1.svg',
  },
  {
    name: 'Lance Korsun',
    role: 'Chief Product Officer',
    image: '/images/team/silhouette-2.svg',
  },
  {
    name: 'Thomas Marsh',
    role: 'Chief Customer Success Officer',
    image: '/images/team/silhouette-3.svg',
  },
  {
    name: 'Rich Shaffer',
    role: 'Chief Financial Officer',
    image: '/images/team/silhouette-4.svg',
  },
  {
    name: 'Himanshu Patel',
    role: 'Head of Technology Services',
    image: '/images/team/silhouette-5.svg',
  },
  {
    name: 'Kapil Maheshwari',
    role: 'Head of DevOps',
    image: '/images/team/silhouette-6.svg',
  },
  {
    name: 'Tom West',
    role: 'Business Development',
    image: '/images/team/silhouette-7.svg',
  },
  {
    name: 'James Harding',
    role: 'Business Development',
    image: '/images/team/silhouette-8.svg',
  },
  {
    name: 'Matt Rambo',
    role: 'Business Development',
    image: '/images/team/silhouette-9.svg',
  },
  {
    name: 'Dharmik Patel',
    role: 'Technical Product Manager',
    image: '/images/team/silhouette-10.svg',
  },
  {
    name: 'Sankit Kolte',
    role: 'Technical Product Owner',
    image: '/images/team/silhouette-11.svg',
  },
  {
    name: 'Emma Wilson',
    role: 'Lead Frontend Engineer',
    image: '/images/team/silhouette-12.svg',
  },
  {
    name: 'James Lee',
    role: 'Lead Backend Engineer',
    image: '/images/team/silhouette-13.svg',
  },
  {
    name: 'Aisha Patel',
    role: 'UX/UI Designer',
    image: '/images/team/silhouette-14.svg',
  },
  {
    name: 'Ryan Garcia',
    role: 'Full Stack Developer',
    image: '/images/team/silhouette-15.svg',
  },
  {
    name: 'Sarah Johnson',
    role: 'Mobile Developer',
    image: '/images/team/silhouette-16.svg',
  },
  {
    name: 'Daniel Kim',
    role: 'DevOps Engineer',
    image: '/images/team/silhouette-17.svg',
  },
  {
    name: 'Zoe Williams',
    role: 'QA Engineer',
    image: '/images/team/silhouette-18.svg',
  },
  {
    name: 'Tyler Robinson',
    role: 'Data Scientist',
    image: '/images/team/silhouette-19.svg',
  },
];

// Mission statement
const missionStatement = {
  title: "Our Mission",
  text: "At Sketchplay, we're on a mission to revolutionize how sports organizations operate. We believe that by providing powerful, intuitive tools for communication, training, and competition management, we can help coaches, athletes, and administrators focus on what matters most: the game. Our platform is designed to eliminate administrative friction, enhance team connectivity, and provide data-driven insights that elevate performance at every level of play."
};

// Values
const values = [
  {
    title: "Innovation",
    description: "We constantly push the boundaries of what's possible in sports technology.",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
      </svg>
    ),
  },
  {
    title: "Community",
    description: "We build technology that strengthens the bonds between athletes, coaches, and fans.",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
      </svg>
    ),
  },
  {
    title: "Excellence",
    description: "We strive for excellence in everything we do, just like the athletes who use our platform.",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 002.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 012.916.52 6.003 6.003 0 01-5.395 4.972m0 0a6.726 6.726 0 01-2.749 1.35m0 0a6.772 6.772 0 01-3.044 0" />
      </svg>
    ),
  },
  {
    title: "Accessibility",
    description: "We create tools that are accessible to sports organizations of all sizes and levels.",
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 010 3.75H5.625a1.875 1.875 0 010-3.75z" />
      </svg>
    ),
  },
];

export default function AboutPage() {
  const { trackFeature } = useAnalytics();
  
  React.useEffect(() => {
    trackFeature('about', 'page_view');
  }, [trackFeature]);

  return (
    <div className="bg-futuristic-black min-h-screen text-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-b from-athletic-accent/20 to-futuristic-black"></div>
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-athletic-highlight/50 to-transparent"></div>
        </div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-20 w-72 h-72 bg-athletic-highlight/10 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-athletic-accent/10 rounded-full filter blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="font-kanit italic font-bold text-4xl md:text-6xl tracking-tight mb-6">
              <span className="block text-white">THE TEAM</span>
              <span className="block bg-gradient-to-r from-athletic-highlight via-athletic-accent to-blue-400 bg-clip-text text-transparent">
                BEHIND SKETCHPLAY
              </span>
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              We're a passionate team of athletes, coaches, and technologists dedicated to revolutionizing sports management.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Statement Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-futuristic-black via-athletic-accent/5 to-futuristic-black"></div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-40 left-20 w-64 h-64 bg-athletic-highlight/10 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-40 right-20 w-72 h-72 bg-athletic-accent/10 rounded-full filter blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-blue-500/5 rounded-full filter blur-3xl animate-pulse"></div>
        </div>
        
        {/* Glowing lines */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-athletic-highlight/50 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-athletic-accent/50 to-transparent"></div>
          <div className="absolute left-[10%] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-blue-500/30 to-transparent"></div>
          <div className="absolute right-[10%] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-blue-500/30 to-transparent"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="max-w-7xl mx-auto"
          >
            {/* Mission header with 3D effect */}
            <div className="text-center mb-16">
              <div className="inline-block relative">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                  className="relative z-10"
                >
                  <h2 className="font-kanit italic font-bold text-5xl md:text-7xl text-white">
                    <span className="bg-gradient-to-r from-athletic-highlight via-athletic-accent to-blue-400 bg-clip-text text-transparent">
                      OUR MISSION
                    </span>
                  </h2>
                </motion.div>
                <div className="absolute -bottom-3 left-0 right-0 h-1 bg-gradient-to-r from-athletic-highlight via-athletic-accent to-blue-400 rounded-full"></div>
                <div className="absolute -bottom-3 left-0 right-0 h-1 bg-gradient-to-r from-athletic-highlight via-athletic-accent to-blue-400 rounded-full blur-md"></div>
                <div className="absolute -bottom-5 left-[5%] right-[5%] h-px bg-gradient-to-r from-athletic-highlight/50 via-athletic-accent/50 to-blue-400/50 rounded-full"></div>
              </div>
            </div>
            
            {/* Mission content with futuristic UI */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              {/* Left side - Mission statement */}
              <motion.div 
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="lg:col-span-7 relative"
              >
                <div className="relative">
                  {/* Decorative elements */}
                  <div className="absolute -left-5 top-0 bottom-0 w-1 bg-gradient-to-b from-athletic-highlight/0 via-athletic-highlight/50 to-athletic-highlight/0"></div>
                  <div className="absolute -top-5 -left-5 w-10 h-10">
                    <div className="absolute top-0 left-0 w-full h-full border-t-2 border-l-2 border-athletic-highlight/50 rounded-tl-lg"></div>
                  </div>
                  <div className="absolute -bottom-5 -left-5 w-10 h-10">
                    <div className="absolute bottom-0 left-0 w-full h-full border-b-2 border-l-2 border-athletic-highlight/50 rounded-bl-lg"></div>
                  </div>
                  
                  {/* Content */}
                  <div className="bg-futuristic-black/60 backdrop-blur-lg border border-white/10 rounded-xl p-8 shadow-athletic-glow">
                    <p className="text-2xl text-gray-300 leading-relaxed">
                      {missionStatement.text}
                    </p>
                    
                    <div className="mt-8 flex items-center">
                      <div className="h-px flex-grow bg-gradient-to-r from-athletic-highlight/50 to-transparent"></div>
                      <div className="px-4 text-athletic-highlight font-kanit font-bold">ESTABLISHED 2023</div>
                      <div className="h-px flex-grow bg-gradient-to-l from-athletic-highlight/50 to-transparent"></div>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              {/* Right side - 3D-like visualization */}
              <motion.div 
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="lg:col-span-5"
              >
                <div className="relative h-full">
                  {/* 3D-like platform */}
                  <div className="absolute inset-0 bg-gradient-to-br from-athletic-highlight/20 to-athletic-accent/20 rounded-2xl transform rotate-6 scale-95 translate-x-4 translate-y-4 blur-sm"></div>
                  <div className="relative bg-futuristic-black/80 backdrop-blur-md border border-white/10 rounded-2xl p-6 shadow-athletic-glow overflow-hidden">
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-athletic-highlight/10 rounded-full filter blur-xl"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-athletic-accent/10 rounded-full filter blur-xl"></div>
                    
                    {/* Values grid with hover effects */}
                    <h3 className="font-kanit text-2xl font-bold text-white mb-6 flex items-center">
                      <span className="w-8 h-8 rounded-lg bg-athletic-highlight/20 text-athletic-highlight flex items-center justify-center mr-3">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                        </svg>
                      </span>
                      OUR VALUES
                    </h3>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {values.map((value, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          whileInView={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5, delay: 0.1 * index }}
                          whileHover={{ 
                            y: -5, 
                            boxShadow: '0 0 20px rgba(99, 102, 241, 0.4)',
                            transition: { duration: 0.2 }
                          }}
                          className="bg-black/30 border border-white/10 rounded-xl p-4 hover:border-athletic-highlight/50 transition-all duration-300"
                        >
                          <div className="w-10 h-10 rounded-lg bg-athletic-highlight/20 text-athletic-highlight flex items-center justify-center mb-3">
                            {value.icon}
                          </div>
                          <h3 className="text-lg font-kanit font-bold text-white mb-1">
                            {value.title}
                          </h3>
                          <p className="text-gray-400 text-sm">
                            {value.description}
                          </p>
                        </motion.div>
                      ))}
                    </div>
                    
                    {/* Decorative dots */}
                    <div className="absolute top-6 right-6 grid grid-cols-3 gap-1">
                      <div className="w-1 h-1 rounded-full bg-athletic-highlight"></div>
                      <div className="w-1 h-1 rounded-full bg-athletic-highlight/70"></div>
                      <div className="w-1 h-1 rounded-full bg-athletic-highlight/40"></div>
                    </div>
                    <div className="absolute bottom-6 left-6 grid grid-cols-3 gap-1">
                      <div className="w-1 h-1 rounded-full bg-athletic-accent/40"></div>
                      <div className="w-1 h-1 rounded-full bg-athletic-accent/70"></div>
                      <div className="w-1 h-1 rounded-full bg-athletic-accent"></div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-futuristic-black via-blue-900/10 to-futuristic-black"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent"></div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="font-kanit italic font-bold text-3xl md:text-5xl text-white mb-4">
              OUR <span className="text-athletic-highlight">TEAM</span>
            </h2>
            <p className="text-xl text-gray-300">
              The talented individuals who make Sketchplay possible
            </p>
          </motion.div>

          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
          >
            {team.map((member, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                className="bg-futuristic-black border border-white/10 rounded-xl overflow-hidden hover:shadow-athletic-blue transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="relative h-64 overflow-hidden">
                  <Image 
                    src={member.image} 
                    alt={member.name}
                    fill
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 25vw"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-futuristic-black to-transparent"></div>
                </div>
                <div className="p-4">
                  <h3 className="text-xl font-kanit font-bold text-white mb-1">
                    {member.name}
                  </h3>
                  <p className="text-athletic-highlight font-medium">
                    {member.role}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Join Us CTA Section */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-futuristic-black to-athletic-accent/30"></div>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-athletic-highlight/50 to-transparent"></div>
        
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-20 w-72 h-72 bg-athletic-highlight/10 rounded-full filter blur-3xl animate-float"></div>
          <div className="absolute bottom-10 right-10 w-64 h-64 bg-athletic-accent/10 rounded-full filter blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={fadeIn}
            className="max-w-4xl mx-auto text-center"
          >
            <h2 className="font-kanit italic font-bold text-3xl md:text-5xl text-white mb-6">
              JOIN OUR <span className="text-athletic-highlight">TEAM</span>
            </h2>
            <p className="text-xl text-gray-300 mb-10">
              We're always looking for talented individuals who are passionate about sports and technology. Check out our open positions and join us in revolutionizing sports management.
            </p>
            
            <Button
              variant="athletic"
              size="xl"
              href="/careers"
              trackingName="about_careers_button"
              trackingLocation="about_page"
              className="font-kanit"
            >
              VIEW OPEN POSITIONS
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
} 